import React, { useState, useEffect } from "react";

import clsx from "clsx";
import Box from "@mui/material/Box";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Card from "@mui/material/Card";
import DoDisturbOnIcon from "@mui/icons-material/DoDisturbOn";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import DeleteIcon from "@mui/icons-material/Delete";
import IconButton from "@mui/material/IconButton";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import Tooltip from "@mui/material/Tooltip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import LinearProgress from "@mui/material/LinearProgress";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import { Link, useNavigate } from "react-router-dom";
import Alert from "@mui/material/Alert";
import { red, green } from "@mui/material/colors";
import axios from "axios";
import i18n from "../../i18n";

// Categories carry a plain wall clock ("Y-m-d H:i:s"), so the day and the hour
// are read off the string — parsing it as an instant would shift the day for
// anyone registering from another timezone.
const categoryDay = c => (c.startsAt ? String(c.startsAt).slice(0, 10) : "");
// Only a time the organizer actually set is shown: without one a category just
// inherits its block's hour, which would put the same time on every row.
const categoryTime = c => (c.explicitStartsAt ? String(c.explicitStartsAt).slice(11, 16) : null);

const dayLabel = day => {
    if (!day) return "";
    const d = new Date(`${day}T00:00`);
    if (Number.isNaN(d.getTime())) return day;
    try {
        return new Intl.DateTimeFormat(i18n.language || "pl", { weekday: "long", day: "2-digit", month: "2-digit" }).format(d);
    } catch (e) {
        return day;
    }
};

const BlocksWithCategories = ({ categories, regEntries, uuid, t, api, code }) => {
    const [blocks, setBlocks] = useState({});
    const [checked, setChecked] = useState([]);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = React.useState(false);
    const [chosen, setChosen] = useState(null);
    const [registered, setRegistered] = useState({});
    // Per-category chosen dances for single-dance categories: { catId: [dances] }.
    const [danceSel, setDanceSel] = useState({});

    const handleClickOpen = event => {
        setChosen(event.currentTarget.getAttribute("cid"));
        setOpen(true);
    };

    const handleClose = () => {
        setChosen(null);
        setOpen(false);
    };
    const navigate = useNavigate();
    useEffect(() => {
        let tmpBlocks = {};

        categories.forEach(c => {
            if (tmpBlocks[c.block.id] === undefined) {
                tmpBlocks[c.block.id] = c.block;
            }
        });
        setBlocks(tmpBlocks);
        setRegistered(regEntries);
    }, []);

    // A block runs on one day; its categories are what say which.
    const blockDay = blockId => {
        const first = categories.find(c => c.block.id === blockId);
        return first ? categoryDay(first) : "";
    };

    const handleToggle = value => () => {
        const currentIndex = checked.indexOf(value);
        const newChecked = [...checked];

        if (currentIndex === -1) {
            newChecked.push(value);
        } else {
            newChecked.splice(currentIndex, 1);
        }

        setChecked(newChecked);
    };

    const toggleDance = (catId, dance) => {
        setDanceSel(prev => {
            const cur = prev[catId] || [];
            const next = cur.indexOf(dance) === -1 ? [...cur, dance] : cur.filter(d => d !== dance);
            return { ...prev, [catId]: next };
        });
    };

    // A checked single-dance category must have at least one dance selected.
    const missingDances = checked.some(id => {
        const cat = categories.find(c => c.id === id);
        return cat && cat.singleDance && (danceSel[id] || []).length === 0;
    });

    const registerToCategories = () => {
        setLoading(true);

        const selectedDances = {};
        checked.forEach(id => {
            const cat = categories.find(c => c.id === id);
            if (cat && cat.singleDance) {
                selectedDances[id] = danceSel[id] || [];
            }
        });

        axios
            .put(`${api.addCategoriesToEntryAnonymously}${uuid}`, { chosen: checked, categories: categories, uuid: uuid, locale: i18n.language, code: code, selectedDances: selectedDances })
            .then(function(response) {
               // console.log(response.data);
                navigate("/validation", { state: { entries: response.data.entries, rejected: response.data.rejected, uuid: uuid, paymentLink: response.data.paymentLink } });
                setLoading(false);
            })
            .catch(function(error) {
                console.log(error);
                setLoading(false);
            });
    };
    const removeFromCategory = () => {
        setLoading(true);
        axios
            .put(`${api.removeCategoryFromEntryAnonymously}${uuid}`, { remove: chosen, locale: i18n.language })
            .then(function(response) {
                // tmpUuid = response.data.uuid;
                setRegistered(response.data.entries);
                setChosen(null);
                setOpen(false);
                setLoading(false);
            })
            .catch(function(error) {
                console.log(error);
                setLoading(false);
            });
    };
    // console.log(code);
    return (
        <Box>
            <Stack spacing={1}>
                {categories.length === 0 && <Alert severity="warning">{t("reg.no_categories_found")}</Alert>}

                {/* Object keys come out in id order, which is the order the blocks were
                    created in, not the order they run in. */}
                {Object.entries(blocks)
                    .sort(([, a], [, b]) => String(a.startsAt).localeCompare(String(b.startsAt)))
                    .map(([k, b]) => {
                    return (
                        <Card sx={{ minWidth: 275 }} key={`b-${b.id}`}>
                            <CardContent>
                                <Typography variant="h5" component="div">
                                    {b.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {dayLabel(blockDay(b.id))}
                                </Typography>
                                <List>
                                    {categories
                                        .filter(c => c.block.id === b.id)
                                        // The list doubles as the timetable once the organizer
                                        // times the categories (festival mode), so it runs in
                                        // start order.
                                        .sort((a, c) => String(a.startsAt).localeCompare(String(c.startsAt)))
                                        .map(c => {
                                            const additionalD = c.additionalDescription !== null ? ` (${c.additionalDescription})` : "";
                                            const startTime = categoryTime(c);
                                            const named = `${startTime ? `${startTime} — ` : ""}${c.name}${additionalD}`;

                                            return (
                                                <React.Fragment key={`d-${c.id}`}>
                                                    {registered.filter(e => e.category.id === c.id).length > 0 && (
                                                        <ListItem
                                                            secondaryAction={
                                                                <Tooltip title={t("reg.remove")}>
                                                                    <IconButton edge="end" aria-label="delete" cid={c.id} onClick={handleClickOpen}>
                                                                        <DeleteIcon sx={{ color: red[500] }} />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            }
                                                        >
                                                            <ListItemIcon>
                                                                <CheckBoxIcon sx={{ color: green[500] }} />
                                                            </ListItemIcon>
                                                            <ListItemText
                                                                id={`cat-lab-${c.id}`}
                                                                primary={named}
                                                                secondary={t("reg.already_registered")}
                                                            />
                                                        </ListItem>
                                                    )}
                                                    {registered.filter(e => e.category.id === c.id).length === 0 && (
                                                        <ListItem disablePadding>
                                                            {(c.registration.open || (code !== undefined && code !== null && code !== "")) && (
                                                                <ListItemButton role={undefined} onClick={handleToggle(c.id)} dense>
                                                                    <ListItemIcon>
                                                                        <Checkbox
                                                                            edge="start"
                                                                            checked={checked.indexOf(c.id) !== -1}
                                                                            tabIndex={-1}
                                                                            disableRipple
                                                                            inputProps={{ "aria-labelledby": `cat-lab-${c.id}` }}
                                                                        />
                                                                    </ListItemIcon>
                                                                    <ListItemText id={`cat-lab-${c.id}`} primary={named} />
                                                                </ListItemButton>
                                                            )}
                                                            {c.registration.open === false && !(code !== undefined && code !== null && code !== "") && (
                                                                <>
                                                                    <ListItemIcon>
                                                                        <DoDisturbOnIcon sx={{ color: red[500] }} />
                                                                    </ListItemIcon>
                                                                    <ListItemText
                                                                        id={`cat-lab-${c.id}`}
                                                                        primary={named}
                                                                        secondary={t("reg.status.reason.reg_open")}
                                                                    />
                                                                </>
                                                            )}
                                                        </ListItem>
                                                    )}
                                                    {c.singleDance && checked.indexOf(c.id) !== -1 && registered.filter(e => e.category.id === c.id).length === 0 && (
                                                        <Box sx={{ pl: 7, pb: 1 }}>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {t("reg.choose_dances")}
                                                            </Typography>
                                                            <Box>
                                                                {(c.dances || []).map(d => (
                                                                    <FormControlLabel
                                                                        key={`${c.id}-${d}`}
                                                                        control={<Checkbox size="small" checked={(danceSel[c.id] || []).indexOf(d) !== -1} onChange={() => toggleDance(c.id, d)} />}
                                                                        label={d}
                                                                    />
                                                                ))}
                                                            </Box>
                                                        </Box>
                                                    )}
                                                </React.Fragment>
                                            );
                                        })}
                                </List>
                            </CardContent>
                        </Card>
                    );
                })}

                {loading && <LinearProgress />}
                <Button
                    variant="contained"
                    color="success"
                    size="large"
                    onClick={registerToCategories}
                    disabled={checked.length === 0 || loading || missingDances}
                    startIcon={<GroupAddIcon />}
                    fullWidth
                >
                    {t("reg.register")}
                </Button>
            </Stack>
            <Dialog open={open} onClose={handleClose} aria-labelledby="alert-dialog-title" aria-describedby="alert-dialog-description">
                <DialogTitle id="alert-dialog-title" className="text-start">
                    {t("reg.remove_title")}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText className="text-start" id="alert-dialog-description">
                        {t("reg.info.remove")}
                    </DialogContentText>
                    {loading && <LinearProgress />}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} autoFocus>
                        {t("back")}
                    </Button>
                    <Button disabled={loading} color="error" onClick={removeFromCategory}>
                        {t("reg.remove")}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default BlocksWithCategories;
